import { useEffect, useRef } from 'react';
import { isModalOpen } from '@vd/ui';

interface ShortcutOptions {
  /** When true, does not fire if focus is on an editable element. */
  ignoreEditable?: boolean;
  /**
   * A page's shortcut, off while a ticket is open over the page
   * (`TicketModal`): the ticket's own keys are the ones that count then, and
   * the page under it is not the one being looked at (Jason, 18 Oct). Every
   * shortcut, page or not, keeps quiet while a dialog is open (`quiet`,
   * TOADO-984).
   */
  page?: boolean;
}

/** How many tickets are open over a page; see `coverPageShortcuts`. */
let covering = 0;

/** Turns page shortcuts off until the returned function is called. */
export function coverPageShortcuts(): () => void {
  covering += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    covering -= 1;
  };
}

/**
 * Whether a shortcut keeps quiet now: a page's while a ticket is open over the
 * page, and every one while a dialog is open (review round 4). None of these
 * hooks is used inside a dialog, and the page under one is inert: a ticket's
 * j, k, [ and ] there changed which ticket an open Delete confirmation was for.
 */
const quiet = (page: boolean) => isModalOpen() || (page && covering > 0);

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

/**
 * Global keyboard shortcut. Binding is by stringified keys like "v", "⌘k", "/", "?".
 * For simplicity, only single-key (optionally with "mod") bindings are supported.
 */
export function useShortcut(key: string, handler: (e: KeyboardEvent) => void, opts: ShortcutOptions = {}) {
  const { ignoreEditable = true, page = false } = opts;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ignoreEditable && isEditableTarget(e.target)) return;
      if (quiet(page)) return;
      if (e.key === key) handlerRef.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, ignoreEditable, page]);
}

/**
 * Multi-key shortcut map. Uses a ref so the map object can change identity
 * every render (which it does, since callers inline `{ v: () => ... }`)
 * without re-registering the window listener — re-registration churn was
 * causing missed keypresses under test.
 */
export function useShortcutMap(map: Record<string, (e: KeyboardEvent) => void>, opts: ShortcutOptions = {}) {
  const { ignoreEditable = true, page = false } = opts;
  const mapRef = useRef(map);
  mapRef.current = map;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ignoreEditable && isEditableTarget(e.target)) return;
      if (quiet(page)) return;
      const fn = mapRef.current[e.key];
      if (fn) fn(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ignoreEditable, page]);
}

/**
 * Modifier-shortcut hook. Fires when Ctrl (or ⌘ on macOS) + the
 * given key are pressed together. Calls preventDefault by default
 * because the typical use case (Ctrl+Enter to submit) wants to
 * suppress the browser's native behavior.
 *
 * Use this for shortcuts that combine the platform mod-key with a
 * single character. For un-modified single keys use `useShortcut`;
 * for a map of un-modified single keys use `useShortcutMap`.
 */
export function useModShortcut(
  key: string,
  handler: (e: KeyboardEvent) => void,
  opts: ShortcutOptions & { preventDefault?: boolean } = {},
) {
  const { ignoreEditable = true, preventDefault = true } = opts;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== key) return;
      // Note: ignoreEditable is OFF by default at the call site here
      // (defaults to ON via the same options shape) because Ctrl+Enter
      // in a textarea is exactly when we want submit-like shortcuts to
      // fire. Callers can override via `ignoreEditable: true`.
      if (ignoreEditable && isEditableTarget(e.target)) return;
      if (quiet(false)) return;
      if (preventDefault) e.preventDefault();
      handlerRef.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, ignoreEditable, preventDefault]);
}
